import React, { useState, useEffect, useContext, useRef } from 'react';

import axios from 'axios';
import { postStudentChat } from '../../../service/studentChatService'
import { buildTutorSystemPrompt, buildTutorUserTurn } from './tutorPrompt'
import { Typography } from '@mui/material';

import { AuthContext } from '../../../context/AuthContext';
import { SessionContext } from '../../../context/SessionContext';
import { MessageContext } from '../../../context/MessageContext';
import { TestResultContext } from '../../../context/TestResultContext';
import { SelectedCodeContext } from "../../../context/SelectedCodeContext";

import { getSubmissionsBySessionId } from '../../../service/submissionService';
import { sendMessageToAI, sendAIResponseToUser, getMessagesBetweenUserAndAI} from '../../../service/chatService';
import { classifyStudentIntent } from '../../../service/gptService';
import { createInteraction } from '../../../service/interactionService';

import Messages from '../Messages';
import ReviewWindow from '../../student-components/ReviewWindow.jsx';
import { showToast } from '../../commonUnit/Toast';
import Loading from '../../commonUnit/Loading';

import { addMessageType} from '../../../tool/aiAssistant';

import '../../../css/chat.scss';

const MemoizedMessages = React.memo(Messages);

const AIChat = () => {
  const initialPrompt = 
  `You are an upbeat, encouraging teaching assistant who helps students understand and python code and related concepts by answering student's question, explaining ideas and giving step-by-step instructions. 
        You must not directly write any code for them. You should guide students in an open-ended way. Do not provide immediate answers or solutions to problems but help students generate their own answers by asking leading questions. 
        Don't provide actual solution code anyway. And every time a student asks a question, you will be given the student's current code automatically with the student's question. 
        Keep your responses brief. Students will ask you questions while they are completing this task. 
        If they ask a broad question, you should divide it into several related concrete questions and ask them what aspect they want to know about, but if they ask about a specific part of the task, focus on that part of the task and do not extend into the other sections.\n 
        The function name much match what's given in the task description. Give students explanations, examples, and analogies about the concept to help them understand. 
        You should provide personalized feedback based on the student's code and question. 
        `
  const gptHost = process.env.REACT_APP_HOST + "/api/v1/gpt/openAI";

  const [prompt, setPrompt] = useState(initialPrompt);
  const [sortQuestion, setSortQuestion] = useState("For the question that the student ask for assistance regarding their code, sort the student's question into one of the following five categories:Task_clarification, Syntax_error_fix, Implementation_challenge, Failing_test_case_fix, Post_completion_code_refinement. Only include the category name in your response. If the student's question is irrelevant to completing the task, respond with 'irrelevant'.");
  const [chatData, setChatData] = useState([
    { role: "system", content: initialPrompt },
  ]);
  const { chatMessage, setChatMessage } = useContext(MessageContext);
  const { selectedCode, currentCode, output } = useContext(SelectedCodeContext);
  const { testResult } = useContext(TestResultContext);
  const { currentUser } = useContext(AuthContext);
  const { session } = useContext(SessionContext);
  const [receiver, setReceiver] = useState('assistant');
  const [keystrokes, setKeystrokes] = useState([]);
  const [isLoadingAIResponse, setIsLoadingAIResponse] = useState(false);
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const chatHistoryRef = useRef(null);
  const [isChatDisabled, setIsChatDisabled] = useState(true);
  const [idleMessageSent, setIdleMessageSent] = useState(false);
  const [isChatUpdated, setIsChatUpdated] = useState(false);
  const CHARACTER_LIMIT = 500;
  const [charCount, setCharCount] = useState(0);
  const [isOverLimit, setIsOverLimit] = useState(false);

  useEffect(() => {
    if (session?.type) {
      if (session.type !== "Vizmental") {
        return;
      }
    }
  }, [session]);

  const addChatData = async (newContent, newSender) => {
    setChatData(prevMessages => {
        const updatedMessages = [...prevMessages, { content: newContent, role: newSender }];
        return updatedMessages;
    });
  };

  useEffect(() => {
    if (selectedCode !== null) {
      getSubmissionsBySessionId(session.id).then((submissions) => {
        const submission = submissions?.find((element) => element.code_id === selectedCode?.id);
      });
    }
  }, [selectedCode]);

  useEffect(() => {
    const updatePromptAndChatData = async () => {
      if (session?.enable_chat || (session?.task && session?.test_list)) {
        const newPrompt = buildTutorSystemPrompt(session);
        setChatData((prevMessages) => {
          const updatedMessages = prevMessages.filter((msg) => msg.role !== "system");
          return [{ role: "system", content: newPrompt }, ...updatedMessages];
        });
        setPrompt(newPrompt);
        setIsChatDisabled(false);
        
      }
    };
    updatePromptAndChatData();
  }, [session, session.enable_chat, session.task, session.test_list]);

  useEffect(() => {
    let isMounted = true;

    const sendWelcomeMessage = async () => {
      if (!currentUser?.id || !session?.id) return;
      
      const messages = await getMessagesBetweenUserAndAI(currentUser.id, session.id);
      
      const welcomeMessage = "Hey I am your AI assistant, feel free to ask me questions if you are stuck!";
      
      // Check if welcome message already exists in the chat history
      const hasWelcomeMessage = messages.some(msg => 
        msg.content?.trim() === welcomeMessage.trim() ||
        msg.content?.includes("AI assistant, feel free to ask")
      );
      
      // Only send if no messages AND welcome message doesn't exist AND component is still mounted
      if (messages.length === 0 && !hasWelcomeMessage && isMounted) {
        await sendAIResponseToUser(welcomeMessage, currentUser, session.id, []);
        await addChatData(welcomeMessage, "assistant");
        setIsChatUpdated(prev => !prev);
      }
    }

    sendWelcomeMessage();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (chatHistoryRef.current) {
      requestAnimationFrame(() => {
        if (chatHistoryRef.current) {
          chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
        }
      });
    }
  }, [displayedMessages, isLoadingAIResponse]);

  useEffect(() => {
    const fetchMessages = async () => {
      const messages = await getMessagesBetweenUserAndAI(currentUser.id, session.id);
      setDisplayedMessages(messages);
    }
    fetchMessages();
  }, [isChatUpdated]);

  const processedInteractionsRef = useRef(new Set());

  useEffect(() => {
    if (displayedMessages.length < 2) return;
    
    const lastMessage = displayedMessages[displayedMessages.length - 1];
    const secondLastMessage = displayedMessages[displayedMessages.length - 2];
    
    // Create unique ID for this interaction pair
    const interactionId = `${secondLastMessage.id}-${lastMessage.id}`;
    
    // Check if we've already processed this interaction
    if (processedInteractionsRef.current.has(interactionId)) {
      return;
    }
    
    if (lastMessage.sender_id === -4 && secondLastMessage.sender_id === currentUser.id) {
      console.log('🟢 Creating interaction record');
      
      // Mark as processed immediately
      processedInteractionsRef.current.add(interactionId);
      
      createInteractionInBackground(secondLastMessage, lastMessage);
    }
  }, [displayedMessages, currentUser.id]);


  if (!session) return null;
  if (!currentUser) return null;
  if (!currentUser.session_id) return <Loading />;

  const handleTextChange = (e) => {
    const text = e.target.value;
    const length = text.length;
    
    setCharCount(length);
    setIsOverLimit(length > CHARACTER_LIMIT);
    setChatMessage(text);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (!e.shiftKey && !isChatDisabled && !isOverLimit) {
        e.preventDefault();
        sendMessage(e);
      }
    }
    if (!isChatDisabled) {
      const now = Date.now();
      const key = e.key;
      const newKeystroke = {
        key: key,
        timestamp: now,
      };

      if (keystrokes.length > 0) {
        const lastKeystroke = keystrokes[keystrokes.length - 1];
        const timeDifference = now - lastKeystroke.timestamp;
        newKeystroke.timeDifference = timeDifference;
      } else {
        newKeystroke.timeDifference = 0;
      }
      setKeystrokes([...keystrokes, newKeystroke]);
    }
  };

  const createInteractionInBackground = async (userMessage, assistantMessage) => {
    try {
      const codeSnapshot = window.editorRef?.current?.getValue?.() || currentCode || '';
      const classification = await classifyStudentIntent(
        session?.task?.description || session?.task || '',
        codeSnapshot,
        displayedMessages.slice(-10).map(m => ({ 
          sender: m.sender_id === -4 ? 'assistant' : 'user', 
          content: m.content 
        })),
        userMessage.content,
        testResult || []
      );
      
      await createInteraction({
        userId: currentUser.id,
        sessionId: session.id,
        questionType: classification.questionType,
        studentIntent: classification.studentIntent,
        studentFacingDescription: classification.studentFacingDescription, 
        codeSnapshot: codeSnapshot,
        messageHistory: [
          { role: 'user', content: userMessage.content, timestamp: userMessage.created_time || new Date() },
          { role: 'assistant', content: assistantMessage.content, timestamp: assistantMessage.created_time || new Date() }
        ]
      });
      
      console.log('✅ Interaction saved:', classification.questionType);
      
    } catch (error) {
      console.error('❌ Failed to save interaction:', error);
      if (error.response) {
        console.error('Backend error:', error.response.data);
      }
    }
  };

  const sendMessage = async (e) => {
    if(!isChatDisabled && !isOverLimit){
      e.preventDefault();
      
      const message = document.getElementById("myTextArea").value;
      if (message.trim()) {
        if (message.length > CHARACTER_LIMIT) {
          showToast(`Message exceeds ${CHARACTER_LIMIT} character limit`, 'error');
          return;
        }
        
        setIdleMessageSent(false);
        setChatMessage('');
        setCharCount(0);
        setIsOverLimit(false);
        setIsChatDisabled(true);
        if (receiver === 'assistant') {
          const res = await sendMessageToAI(message, currentUser, session.id, keystrokes);
          
          if (res && res.id) {
            document.getElementById("myTextArea").value = "";
            setKeystrokes([]);
            await addChatData(message, "user");
            setIsChatUpdated(!isChatUpdated);
            
            try {
              const catagory = await axios.post(gptHost, { 
                messages: [
                  {role:"system", content: sortQuestion},
                  {role: "user", content: message}
                ] 
              });
              await addMessageType(res.id, catagory.data.content);
            } catch (error) {
              console.error('Failed to categorize message:', error);
            }
            
            await askAI(message);
          } else {
            console.error('Failed to send message or missing message ID');
            showToast('Failed to send message', 'error');
          }
        }
        setIsChatDisabled(false);
      } else {
        showToast('Chat is disabled', 'warning');
      }
    }
  };

  const askAI = async (studentPrompt) => {
      const newPrompt = buildTutorUserTurn({ studentPrompt, currentCode, session, testResult, output });

      console.log("newPrompt", newPrompt);
      setIsLoadingAIResponse(true);

      try {
        // Through /gpt/studentChat so the session's AI Chat Policy is applied server-side
        // (studentChatService.js). The categorizer call above stays on /gpt/openAI - it is
        // not a student-facing reply.
        const reply = await postStudentChat(session, [...chatData, { role: "user", content: newPrompt }]);
        const responseMessage = reply.content;
        await sendAIResponseToUser(responseMessage, currentUser, session.id, []);
        
        await addChatData(responseMessage, "assistant");
        setIsChatUpdated(!!isChatUpdated);
        
      } catch (error) {
        console.error(error);
        addChatData("Sorry, something went wrong. Please try again.", "assistant");
      } finally {
        setIsLoadingAIResponse(false);
      }
  };

  return (
  <>
    {
      !session?.enable_chat ? (
        session?.closeSessionType === 'closed with survey' ? (
          <ReviewWindow />
        ) : (
          <Typography
            variant="h6"
            style={{ textAlign: 'center', marginTop: '20px' }}
          >
            AI Chat is currently disabled for this session.
          </Typography>
        )
      ) : (
        <div className="chat-container" style={{ height: '100%' }}>
          <div className="chat" style={{ height: '100%' }}>
            <div className="chat-history" ref={chatHistoryRef}>
              <MemoizedMessages messages={displayedMessages} />
            </div>
            <form className="message-form" style={{ position: 'relative' }}>
              <textarea
                className="message-input"
                id="myTextArea"
                placeholder={isChatDisabled ? 'Generating...' : 'Use Shift + Enter for new line'}
                onChange={handleTextChange}
                onKeyDown={handleKeyPress}
                style={{
                  borderColor: isOverLimit ? '#d32f2f' : undefined,
                  paddingBottom: '28px'
                }}
              />
              <div style={{ 
                position: 'absolute',
                bottom: '8px',
                right: '90px',
                fontSize: '11px',
                pointerEvents: 'none',
                padding: '4px 8px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '4px',
                zIndex: 10
              }}>
                <span style={{ 
                  color: isOverLimit ? '#d32f2f' : charCount > CHARACTER_LIMIT * 0.9 ? '#ed6c02' : '#999',
                  fontWeight: isOverLimit ? 'bold' : 'normal'
                }}>
                  {charCount}/{CHARACTER_LIMIT}
                  {isOverLimit && ' ⚠'}
                </span>
              </div>
              <button
                type="submit"
                className="send-button"
                onClick={sendMessage}
                disabled={isChatDisabled || isOverLimit}
                style={{
                  backgroundColor: (isChatDisabled || isOverLimit) ? '#efefef' : '#1976d2',
                  cursor: (isChatDisabled || isOverLimit) ? 'not-allowed' : 'pointer'
                }}
              >
                Send
              </button>
            </form>
          </div>
        </div>
      )
    }
  </>
);
}

export default AIChat;